import type { ReactNode } from 'react';
import { EmptyState } from '@/shared/components/feedback/EmptyState';
import { LoadingState } from '@/shared/components/feedback/LoadingState';
import { useCapabilities } from '../hooks/useCapabilities';

/**
 * Renders workspace pages only when Core serves the workspace.
 *
 * Navigation already hides the entry point, but a bookmarked or typed `/p` link still
 * arrives here; without the gate it would send requests an older Core cannot answer.
 */
export function WorkspaceGate({
  children,
  feature = 'projectWorkspace',
}: {
  readonly children: ReactNode;
  /** The capability the page needs; documents pages need the workspace and documents. */
  readonly feature?: 'projectWorkspace' | 'projectDocuments';
}): React.JSX.Element {
  const { capabilities, isPending } = useCapabilities();

  if (isPending)
    return <LoadingState label="Checking what this server supports…" testID="workspace-gate-loading" />;

  if (!capabilities.projectWorkspace || (feature === 'projectDocuments' && !capabilities.projectDocuments)) {
    return (
      <EmptyState
        title={
          feature === 'projectDocuments'
            ? 'Documents are not available yet'
            : 'Projects are not available yet'
        }
        description={
          feature === 'projectDocuments'
            ? 'This Conbent server does not provide project documents.'
            : 'This Conbent server does not provide the project workspace. Your Revit models are under Projects.'
        }
        testID="workspace-unavailable"
      />
    );
  }

  return <>{children}</>;
}
